import { memo, useMemo } from 'react'

import * as Styled from './styled'
import { IFadeOverlayProps } from './types'
import {
  FADE_FILL_COLOR,
  FADE_FILL_OPACITY,
  FADE_LINE_COLOR,
  FADE_LINE_WIDTH
} from './consts'

const clampPercent = (value: number) => Math.min(100, Math.max(0, value))

/**
 * Draws the fade-in / fade-out wedges over a timeline item without touching the
 * waveform or thumbnails underneath. Each wedge is a triangle (item edge →
 * top corner → fade-end point at the top) filled with a translucent overlay,
 * plus a 1px diagonal marking the fade ramp. Fade widths are expressed as a
 * percentage of the item's duration, so the SVG scales with the item box.
 */
const FadeOverlay = ({
  audioEffects,
  duration,
  className
}: IFadeOverlayProps) => {
  const { fadeInPercent, fadeOutPercent } = useMemo(() => {
    if (!audioEffects?.length || !duration) {
      return { fadeInPercent: 0, fadeOutPercent: 0 }
    }

    const fadeIn =
      audioEffects.find(effect => effect.type === 'fade-in')?.meta.duration ?? 0
    const fadeOut =
      audioEffects.find(effect => effect.type === 'fade-out')?.meta.duration ??
      0

    return {
      fadeInPercent: clampPercent((fadeIn / duration) * 100),
      fadeOutPercent: clampPercent((fadeOut / duration) * 100)
    }
  }, [audioEffects, duration])

  if (!fadeInPercent && !fadeOutPercent) return null

  return (
    <Styled.Svg
      className={className}
      viewBox="0 0 100 100"
      preserveAspectRatio="none"
    >
      {fadeInPercent > 0 && (
        <>
          <polygon
            points={`0,100 0,0 ${fadeInPercent},0`}
            fill={FADE_FILL_COLOR}
            fillOpacity={FADE_FILL_OPACITY}
          />
          <line
            x1={0}
            y1={100}
            x2={fadeInPercent}
            y2={0}
            stroke={FADE_LINE_COLOR}
            strokeWidth={FADE_LINE_WIDTH}
            vectorEffect="non-scaling-stroke"
          />
        </>
      )}
      {fadeOutPercent > 0 && (
        <>
          <polygon
            points={`100,100 100,0 ${100 - fadeOutPercent},0`}
            fill={FADE_FILL_COLOR}
            fillOpacity={FADE_FILL_OPACITY}
          />
          <line
            x1={100}
            y1={100}
            x2={100 - fadeOutPercent}
            y2={0}
            stroke={FADE_LINE_COLOR}
            strokeWidth={FADE_LINE_WIDTH}
            vectorEffect="non-scaling-stroke"
          />
        </>
      )}
    </Styled.Svg>
  )
}

export default memo(FadeOverlay)
